import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LIGHT_THEME_CSS, DARK_THEME_CSS, BASE_CSS } from './src/theme';
import { useAppStore } from './src/store/useAppStore';
import { SessionProvider } from './src/context/SessionContext';
import { UploadProvider } from './src/context/UploadContext';
import RootGate from './src/navigation/RootGate';
import WebShell from './src/navigation/WebShell';
import BrowseWrapper, { RecentWrapper, StarredWrapper, SharedWrapper } from './src/screens/BrowseWrapper';
import Trash from './src/screens/Trash';
import Transfers from './src/screens/Transfers';
import Requests from './src/screens/Requests';
import Storage from './src/screens/Storage';
import Members from './src/screens/Members';
import StorageRequests from './src/screens/StorageRequests';
import Admin from './src/screens/Admin';
import Billing from './src/screens/Billing';
import SharedView from './src/screens/SharedView';
import PublicTransferView from './src/screens/PublicTransferView';
import PublicRequestView from './src/screens/PublicRequestView';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Inject global CSS and sync theme on html element from persisted store state
function ThemeInit() {
  const theme = useAppStore(s => s.theme);

  useEffect(() => {
    // Inject CSS once
    const styleId = 'sk-drive-theme';
    if (!document.getElementById(styleId)) {
      const el = document.createElement('style');
      el.id = styleId;
      el.textContent = LIGHT_THEME_CSS + DARK_THEME_CSS + BASE_CSS;
      document.head.appendChild(el);
    }
    // Apply theme attribute to <html> so CSS variables resolve everywhere
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return null;
}

// SessionProvider + RootGate only wrap the authenticated shell — the public
// share-link route below never triggers a platform session probe/redirect.
function GatedShell() {
  return (
    <SessionProvider>
      <RootGate>
        <UploadProvider>
          <WebShell />
        </UploadProvider>
      </RootGate>
    </SessionProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeInit />
      <BrowserRouter>
        <Routes>
          {/* Public — anonymous share links. These must stay outside GatedShell
              so a visitor with no session is never bounced to platform auth. */}
          <Route path="/s/:token" element={<SharedView />} />
          <Route path="/t/:token" element={<PublicTransferView />} />
          <Route path="/r/:token" element={<PublicRequestView />} />

          {/* App shell — gated by RootGate; no local login screen, the
              SK Platform owns all auth UI and this bounces there. */}
          <Route element={<GatedShell />}>
            <Route index element={<Navigate to="/browse" replace />} />
            <Route path="/browse" element={<BrowseWrapper />} />
            <Route path="/space/:spaceId" element={<BrowseWrapper />} />
            <Route path="/recent" element={<RecentWrapper />} />
            <Route path="/shared" element={<SharedWrapper />} />
            <Route path="/starred" element={<StarredWrapper />} />
            <Route path="/trash" element={<Trash />} />
            <Route path="/requests" element={<Requests />} />
            <Route path="/transfers" element={<Transfers />} />
            <Route path="/storage" element={<Storage />} />
            <Route path="/members" element={<Members />} />
            <Route path="/storagereq" element={<StorageRequests />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/account" element={<Billing />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/browse" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
